import React from 'react';
import { Link } from 'react-router-dom';
import { ScheduleNowSession, ScheduleSession } from '../../types/types';
import { canPlayInApp, MergedLive, OnAirCard, PLENUM_TITLE, ScheduledNotAired } from '../../utils/live';
import { useBreakpoint } from '../../useBreakpoint';
import Grid from '../../layout/Grid';
import { color, font, radius, shadow, space, weight } from '../../theme';
import { Card } from '../Card';
import ListRow from '../ListRow';
import Icon from '../Icon';
import { externalWatchUrl, hhmm, WatchLiveButton } from './PlayButtons';

/* SchedulePage's "מתקיים עכשיו" (decisions 1A and LB, owner 2026-10-17), from utils/live
   mergeLive over the meta/scheduleNow rows:
   - With a fresh feed: a "מתקיים עכשיו · N" heading, then what's on air (the plenum first, with
     who speaks now; committees; a broadcast on a break says "בהפסקה" and has no red ring),
     each with "צפייה" (the in-app player, or a link out when the stream can't play here). After
     them, muted, "לפי הלו״ז, בלי שידור": sessions the schedule calls live that aren't on air,
     with the reason and no watch button.
   - Without one: every session the time rule calls live, as cards that say "מתקיים עכשיו",
     as before the feed. The plenum card still plays in the app (the Knesset channel's fixed
     stream, PLENUM_STREAM_URL); committee cards link out to watch.
   Desktop: the cards side by side. */

// A meta/scheduleNow row, plus the month session's sessionType when it's loaded (a closed
// session's "not aired" reason comes from it)
export type NowRow = ScheduleNowSession & { sessionType?: string | null };

// SchedulePage injects the 'pulse-dot' keyframe while it's mounted
const LiveDot = () => (
  <span style={{ width: 8, height: 8, borderRadius: '50%', background: color.live, display: 'inline-block', animation: 'pulse-dot 1.4s ease-in-out infinite', flexShrink: 0 }} />
);

// The schedule's agenda line: the first item and how many more, else just the count
const scheduleAgenda = (s: NowRow | null, full: ScheduleSession | undefined): string | null => {
  if (!s || s.agendaCount <= 0) return null;
  const first = full && full.agendaItems.length > 0 ? full.agendaItems[0].name : null;
  if (first === null) return `${s.agendaCount} נושאים על סדר היום`;
  return s.agendaCount > 1 ? `${first} ועוד ${s.agendaCount - 1}` : first;
};

const CLAMP_2: React.CSSProperties = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

const OnAirCardView = ({ card, fromFeed, full }: { card: OnAirCard<NowRow>; fromFeed: boolean; full: ScheduleSession | undefined }) => {
  const since = hhmm(card.since);
  const isPlenum = card.kind === 'plenum';
  const speaker = card.speakers.length > 0 ? card.speakers[0] : null;
  const fromSchedule = scheduleAgenda(card.session, full);
  const agenda = isPlenum
    ? (card.subject ? `על סדר היום: ${card.subject}` : fromSchedule)
    : (card.subject || fromSchedule);
  const firstItem = full && full.agendaItems.length > 0 ? full.agendaItems[0].name : null;

  const body = (
    <>
      <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, fontSize: font.caption, fontWeight: weight.bold, color: card.paused ? color.textSecondary : color.oppositionText }}>
        {card.paused ? <Icon name="pause" size={12} strokeWidth={2.2} /> : <LiveDot />}
        {fromFeed ? (card.paused ? 'בהפסקה' : 'בשידור') : 'מתקיים עכשיו'}
        {since && ` · מאז ${since}`}
      </span>
      <span style={{ fontSize: font.body, fontWeight: weight.bold, color: color.text }}>{card.title}</span>
      {speaker && (
        <span style={{ display: 'flex', alignItems: 'center', gap: space.sm + 2, padding: `${space.sm}px ${space.sm + 2}px`, borderRadius: radius.md + 2, background: color.surfaceMuted, marginTop: space.xxs }}>
          <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, background: color.primarySoft, color: color.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="mic" size={18} />
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <span style={{ fontSize: font.caption, color: color.textSecondary }}>מדבר/ת עכשיו</span>
            <span style={{ fontSize: font.label, fontWeight: weight.bold, color: color.text }}>
              {speaker.name}
              {speaker.faction && <span style={{ fontWeight: weight.regular, color: color.textSecondary }}> · {speaker.faction}</span>}
            </span>
          </span>
        </span>
      )}
      {agenda && <span style={{ fontSize: font.small, color: color.textSecondary, lineHeight: 1.5, ...CLAMP_2 }}>{agenda}</span>}
    </>
  );
  const bodyStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: space.xs, textDecoration: 'none' };
  const canWatch = canPlayInApp(card.streamUrl) || externalWatchUrl(card) !== null;

  return (
    // A column, so side by side (desktop) the watch buttons line up at the bottom of the cards
    <Card style={{ display: 'flex', flexDirection: 'column', boxShadow: card.paused ? shadow.card : `${shadow.card}, inset 0 0 0 1.5px ${color.live}` }}>
      {card.committeeDocId
        ? <Link to={`/committees/${card.committeeDocId}`} state={{ from: 'לוח הזמנים' }} style={bodyStyle}>{body}</Link>
        : <div style={bodyStyle}>{body}</div>}
      {canWatch && (
        <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap', marginTop: 'auto', paddingTop: space.md }}>
          <WatchLiveButton card={card} agenda={firstItem} />
        </div>
      )}
    </Card>
  );
};

// One muted row: the session, its start time and why it isn't on air
const NotAiredRow = ({ item, divider }: { item: ScheduledNotAired<NowRow>; divider: boolean }) => {
  const s = item.session;
  const name = s.type === 'plenum' ? PLENUM_TITLE : (s.committeeName || s.sessionName || '');
  const time = hhmm(s.date);
  return (
    <ListRow
      divider={divider}
      title={<span style={{ fontSize: font.label, color: color.textSecondary }}>{name}</span>}
      subtitle={<span style={{ color: color.textTertiary }}>{time ? `${time} · ` : ''}{item.reasonLabel}</span>}
    />
  );
};

interface LiveNowSectionProps {
  merged: MergedLive<NowRow>;
  // The loaded month session behind a card (for its agenda items), by session key
  fullSession: (key: string) => ScheduleSession | undefined;
}

const LiveNowSection = ({ merged, fullSession }: LiveNowSectionProps) => {
  const { isDesktop } = useBreakpoint();
  const { onAir, scheduledNotAired, fromFeed } = merged;
  if (onAir.length === 0 && scheduledNotAired.length === 0) return null;

  return (
    <section aria-label="מתקיים עכשיו" style={{ display: 'flex', flexDirection: 'column', gap: space.sm + 2, marginBottom: space.xl }}>
      {fromFeed && onAir.length > 0 && (
        <h2 style={{ display: 'flex', alignItems: 'center', gap: space.sm, margin: `${space.xs}px 0 0`, fontSize: font.label, fontWeight: weight.bold, color: color.oppositionText }}>
          <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: color.live, flexShrink: 0 }} />
          מתקיים עכשיו · {onAir.length}
        </h2>
      )}
      {onAir.length > 0 && (
        <Grid onlyOnDesktop minColWidth={300} gap="12px">
          {onAir.map(card => <OnAirCardView key={card.key} card={card} fromFeed={fromFeed} full={fullSession(card.key)} />)}
        </Grid>
      )}

      {scheduledNotAired.length > 0 && (
        <>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: space.sm, margin: `${space.sm}px 0 0`, fontSize: font.small, fontWeight: weight.bold, color: color.textSecondary }}>
            <Icon name="calendar" size={15} />
            לפי הלו״ז, בלי שידור · {scheduledNotAired.length}
          </h2>
          {isDesktop ? (
            <Grid minColWidth={300} gap="12px">
              {scheduledNotAired.map(item => <Card key={item.key} padding={0}><NotAiredRow item={item} divider={false} /></Card>)}
            </Grid>
          ) : (
            <Card padding={0}>
              {scheduledNotAired.map((item, i) => <NotAiredRow key={item.key} item={item} divider={i > 0} />)}
            </Card>
          )}
        </>
      )}
    </section>
  );
};

export default LiveNowSection;
